import { Api } from "../axios-config/AxiosConfig";

export interface IPessoaSolicitanteReserva {
  idPessoa: number;
  ativo: boolean;
  nome: string;
  nomeTratado: string;
  cpfCnpj: string;
  tipoPessoa: number;
  idUsuarioAuth: string;
}

export interface IUsuarioLogadoInfoReserva {
  idUsuario: number;
  nomeUsuario: string;
  idsSetores: string[];
  idTenant: string;
}

export interface IArquivoReservaPayload {
  nomeArquivo: string;
  arquivo: File;
}

export interface IArquivoReservaDetalhe {
  idArquivo: string;
  nomeArquivo: string;
}

export interface IDownloadArquivoReservaResponse {
  arquivo: Blob;
  nomeArquivo: string;
}

interface IArquivoReservaApiPayload {
  nomeArquivo: string;
  conteudoBase64: string;
}

interface ICriarReservaApiPayload {
  idEstabelecimento: string;
  dataHoraInicio: string;
  dataHoraFim: string;
  tipoSolicitante: number;
  idPessoaSolicitante: string;
  nomePessoaSolicitante: string;
  idPessoaSolicitanteTerceiro: string;
  nomeSolicitanteTerceiro: string;
  observacao: string;
  idLocal: string;
  idsRecursos: string[];
  arquivos: IArquivoReservaApiPayload[];
}

export interface IReservaMinhaReserva {
  id: string;
  numeroReserva: string;
  local: string;
  recurso: string;
  arquivos: IArquivoReservaDetalhe[];
  dataReserva: string;
  horaInicio: string;
  horaFim: string;
  observacao: string;
  solicitante: string;
  utilizada: boolean;
  cancelada: boolean;
  status: number;
  statusDescricao: string;
  motivoCancelamento: string;
  dataCancelamento: string;
  usuarioResponsavelCancelamento: string;
  podeVisualizar: boolean;
  podeEditarSituacao: boolean;
  podeAlterarUtilizada: boolean;
  podeCancelar: boolean;
}

export interface IFiltrosMinhasReservas {
  data?: string;
  numeroReserva?: string;
}

export interface IListagemMinhasReservas {
  items: IReservaMinhaReserva[];
  total: number;
}

export interface IIndicadoresMinhasReservas {
  reservasParaHoje: number;
  reservasFuturas: number;
}

export interface IAtualizarSituacaoReservaPayload {
  utilizada: boolean;
  status: number;
  motivoCancelamento: string;
  origemCancelamento?: number;
}

const STATUS_ATIVA = 1;
export const ORIGEM_CANCELAMENTO_MINHA_RESERVA = 1;
const STATUS_CANCELADA = 2;
const TIPO_SETOR_RESPONSAVEL = 1;
const TIPO_SETOR_VISUALIZACAO = 2;

export interface ICriarReservaPayload {
  idEstabelecimento: string;
  dataHoraInicio: string;
  dataHoraFim: string;
  tipoSolicitante: number;
  idPessoaSolicitante: string;
  nomePessoaSolicitante: string;
  idPessoaSolicitanteTerceiro: string;
  nomeSolicitanteTerceiro: string;
  observacao: string;
  idLocal: string;
  idsRecursos: string[];
  arquivos: IArquivoReservaPayload[];
}

export interface IErroCriarReserva extends Error {
  status?: number;
  detalhes?: unknown;
}

const converterArquivoParaBase64 = (arquivo: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const resultado = reader.result;

      if (typeof resultado !== "string") {
        reject(new Error("Não foi possível converter o arquivo para Base64."));
        return;
      }

      const conteudoBase64 = resultado.includes(",")
        ? resultado.split(",")[1]
        : resultado;

      resolve(conteudoBase64);
    };

    reader.onerror = () => {
      reject(new Error("Não foi possível ler o arquivo selecionado."));
    };

    reader.readAsDataURL(arquivo);
  });

const montarPayloadCriacaoReserva = async (
  dados: ICriarReservaPayload,
): Promise<ICriarReservaApiPayload> => {
  const arquivos = await Promise.all(
    dados.arquivos.map(async ({ nomeArquivo, arquivo }) => ({
      nomeArquivo,
      conteudoBase64: await converterArquivoParaBase64(arquivo),
    })),
  );

  return {
    ...dados,
    arquivos,
  };
};

export interface IFiltrosDisponibilidadeReserva {
  estabelecimentoId: string;
  dataReserva: string;
  horaInicio: string;
  horaFim: string;
  idEstabelecimento?: string;
  pagina?: number;
  limite?: number;
  tamanhoPagina?: number;
  categoriaId?: string;
  idCategoria?: string;
  busca?: string;
  nome?: string;
}

export interface IItemDisponivelReserva {
  id: string | number;
  nomeExibicao: string;
  categoriaId?: string | number;
  categoriaNome?: string;
  permiteRecurso?: boolean;
  tipo?: string;
  predio?: string;
  bloco?: string;
  sala?: string;
  andar?: string;
  capacidade?: number;
  patrimonio?: string;
  [key: string]: unknown;
}

export interface ICategoriaDisponivelReserva {
  id: string | number;
  nomeExibicao: string;
}

export interface IListagemDisponibilidadeReserva {
  items: IItemDisponivelReserva[];
  totalRegistros: number;
  pagina: number;
  limite: number;
}

type TTipoDisponibilidade = "local" | "recurso";

const tipoParaCodigo = (tipo: TTipoDisponibilidade): number =>
  tipo === "recurso" ? 1 : 2;

const toOptionalBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();

    if (normalized === "true" || normalized === "1") {
      return true;
    }

    if (normalized === "false" || normalized === "0") {
      return false;
    }
  }

  return undefined;
};

const toOptionalNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsedValue = Number(value);

    if (!Number.isNaN(parsedValue)) {
      return parsedValue;
    }
  }

  return undefined;
};

const toStringSafe = (value: unknown): string => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }

  return "";
};

const toRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return undefined;
};

const normalizarIdsSetores = (ids: string[]): string[] =>
  Array.from(new Set(ids.map((id) => id.trim()).filter(Boolean)));

const getFirstMatch = <T>(
  source: Record<string, unknown>,
  keys: string[],
  parser: (value: unknown) => T | undefined,
): T | undefined => {
  for (const key of keys) {
    const parsedValue = parser(source[key]);

    if (parsedValue !== undefined) {
      return parsedValue;
    }
  }

  return undefined;
};

const extrairIdsSetores = (
  value: unknown,
  tiposPermitidos?: number[],
): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return normalizarIdsSetores(
    value.flatMap((item) => {
      if (typeof item === "string" || typeof item === "number") {
        return [String(item).trim()];
      }

      const record = toRecord(item);

      if (!record) {
        return [];
      }

      const tipo = getFirstMatch(record, ["tipo", "Tipo"], toOptionalNumber);

      if (tiposPermitidos?.length) {
        if (tipo !== undefined && !tiposPermitidos.includes(tipo)) {
          return [];
        }

        if (tipo === undefined) {
          const setorResponsavel = toOptionalBoolean(
            record.setorResponsavel ?? record.SetorResponsavel,
          );

          if (
            setorResponsavel === true &&
            !tiposPermitidos.includes(TIPO_SETOR_RESPONSAVEL)
          ) {
            return [];
          }

          if (
            setorResponsavel === false &&
            !tiposPermitidos.includes(TIPO_SETOR_VISUALIZACAO)
          ) {
            return [];
          }
        }
      }

      const idSetor =
        getFirstMatch(
          record,
          ["idSetor", "IdSetor", "id", "Id"],
          (rawValue) => {
            const parsedValue = toStringSafe(rawValue);
            return parsedValue || undefined;
          },
        ) ?? "";

      return idSetor ? [idSetor] : [];
    }),
  );
};

const normalizarPessoaSolicitante = (
  item: Record<string, unknown>,
): IPessoaSolicitanteReserva => ({
  idPessoa: Number(item.idPessoa ?? 0),
  ativo: Boolean(item.ativo),
  nome: String(item.nome ?? ""),
  nomeTratado: String(item.nomeTratado ?? item.nome ?? ""),
  cpfCnpj: String(item.cpfCnpj ?? ""),
  tipoPessoa: Number(item.tipoPessoa ?? 0),
  idUsuarioAuth: String(item.idUsuarioAuth ?? ""),
});

const normalizarItemDisponibilidade = (
  item: Record<string, unknown>,
): IItemDisponivelReserva => ({
  ...item,
  id:
    (item.id as string | number | undefined) ??
    (item.localId as string | number | undefined) ??
    (item.recursoId as string | number | undefined) ??
    "",
  nomeExibicao:
    (item.nomeExibicao as string | undefined) ??
    (item.nome as string | undefined) ??
    (item.descricao as string | undefined) ??
    "",
  categoriaId:
    (item.categoriaId as string | number | undefined) ??
    (item.idCategoria as string | number | undefined),
  categoriaNome:
    (item.categoriaNome as string | undefined) ??
    (item.nomeCategoria as string | undefined) ??
    (item.categoria as string | undefined),
  permiteRecurso:
    toOptionalBoolean(item.permiteRecurso) ??
    toOptionalBoolean(item.permiteRecursos) ??
    toOptionalBoolean(item.permiteVincularRecurso) ??
    toOptionalBoolean(item.PermiteVincularRecurso) ??
    toOptionalBoolean(item.permiteRecursoReserva) ??
    toOptionalBoolean(item.PermiteRecursoReserva) ??
    toOptionalBoolean(item.permiteRescursoReserva) ??
    toOptionalBoolean(item.PermiteRescursoReserva),
});

const extrairArray = (data: unknown): Record<string, unknown>[] => {
  if (Array.isArray(data)) {
    return data as Record<string, unknown>[];
  }

  if (data && typeof data === "object") {
    const registro = data as Record<string, unknown>;

    if (Array.isArray(registro.items)) {
      return registro.items as Record<string, unknown>[];
    }

    if (Array.isArray(registro.data)) {
      return registro.data as Record<string, unknown>[];
    }

    if (Array.isArray(registro.registros)) {
      return registro.registros as Record<string, unknown>[];
    }

    if (Array.isArray(registro.resultados)) {
      return registro.resultados as Record<string, unknown>[];
    }
  }

  return [];
};

const extrairTotal = (
  data: unknown,
  fallback: number,
  headers: Record<string, unknown>,
): number => {
  if (data && typeof data === "object") {
    const registro = data as Record<string, unknown>;
    const totalObj =
      (registro.totalRegistros as number | undefined) ??
      (registro.totalCount as number | undefined) ??
      (registro.quantidadeTotal as number | undefined);

    if (typeof totalObj === "number") {
      return totalObj;
    }
  }

  const totalHeader = headers["x-total-count"];
  const totalNumerico = Number(totalHeader);

  if (!Number.isNaN(totalNumerico) && totalNumerico > 0) {
    return totalNumerico;
  }

  return fallback;
};

const extrairStatusReserva = (item: Record<string, unknown>): number => {
  return (
    getFirstMatch(
      item,
      ["status", "idStatus", "situacao", "idSituacao"],
      toOptionalNumber,
    ) ?? 0
  );
};

const normalizarArquivoReserva = (
  item: Record<string, unknown>,
): IArquivoReservaDetalhe => ({
  idArquivo:
    getFirstMatch(item, ["idArquivo", "IdArquivo", "id", "Id"], (value) => {
      const parsedValue = toStringSafe(value);
      return parsedValue || undefined;
    }) ?? "",
  nomeArquivo:
    getFirstMatch(
      item,
      ["nomeArquivo", "NomeArquivo", "nome", "Nome"],
      (value) => {
        const parsedValue = toStringSafe(value);
        return parsedValue || undefined;
      },
    ) ?? "",
});

const normalizarReservaMinha = (
  item: Record<string, unknown>,
): IReservaMinhaReserva => {
  const localObj = toRecord(item.local);
  const recursosArray = Array.isArray(item.recursos)
    ? (item.recursos as Record<string, unknown>[])
    : [];

  const numeroReserva =
    getFirstMatch(
      item,
      ["numeroReserva", "nroReserva", "codigoReserva", "numero"],
      (value) => {
        const parsedValue = toStringSafe(value);
        return parsedValue || undefined;
      },
    ) ?? "";

  const idNumerico =
    getFirstMatch(
      item,
      [
        "id",
        "idReserva",
        "reservaId",
        "reservaID",
        "codigo",
        "codigoReserva",
        "idSolicitacao",
        "idSolicitacaoReserva",
      ],
      toOptionalNumber,
    ) ?? toOptionalNumber(numeroReserva);
  const idTexto =
    getFirstMatch(
      item,
      [
        "id",
        "idReserva",
        "reservaId",
        "reservaID",
        "codigo",
        "codigoReserva",
        "idSolicitacao",
        "idSolicitacaoReserva",
      ],
      (value) => {
        const parsedValue = toStringSafe(value);
        return parsedValue || undefined;
      },
    ) ?? "";
  const id = idTexto || (idNumerico !== undefined ? String(idNumerico) : "");

  const status = extrairStatusReserva(item);
  const statusDescricao =
    getFirstMatch(
      item,
      ["statusDescricao", "descricaoStatus", "situacaoDescricao", "situacao"],
      (value) => {
        const parsedValue = toStringSafe(value);
        return parsedValue || undefined;
      },
    ) ?? "";
  const canceladaPorStatusTexto = statusDescricao
    .trim()
    .toLowerCase()
    .includes("cancel");
  const canceladaPorFlag =
    getFirstMatch(item, ["cancelada", "isCancelada"], toOptionalBoolean) ??
    false;
  const cancelada = canceladaPorFlag || canceladaPorStatusTexto || status === 2;

  const utilizada =
    getFirstMatch(
      item,
      ["utilizada", "isUtilizada", "foiUtilizada"],
      toOptionalBoolean,
    ) ?? false;

  const dataHoraInicio =
    getFirstMatch(item, ["dataHoraInicio", "inicio", "dataInicio"], (value) => {
      const parsedValue = toStringSafe(value);
      return parsedValue || undefined;
    }) ?? "";
  const dataHoraFim =
    getFirstMatch(item, ["dataHoraFim", "fim", "dataFim"], (value) => {
      const parsedValue = toStringSafe(value);
      return parsedValue || undefined;
    }) ?? "";

  const localNome =
    getFirstMatch(item, ["local", "nomeLocal", "localNome"], (value) => {
      const parsedValue = toStringSafe(value);
      return parsedValue || undefined;
    }) ??
    getFirstMatch(localObj || {}, ["nome", "descricao"], (value) => {
      const parsedValue = toStringSafe(value);
      return parsedValue || undefined;
    }) ??
    "";

  const recursoTextoDireto =
    getFirstMatch(item, ["recurso", "nomeRecurso", "recursoNome"], (value) => {
      const parsedValue = toStringSafe(value);
      return parsedValue || undefined;
    }) ?? "";

  const recursosNomes = recursosArray
    .map((recurso) =>
      getFirstMatch(recurso, ["nome", "descricao", "nomeExibicao"], (value) => {
        const parsedValue = toStringSafe(value);
        return parsedValue || undefined;
      }),
    )
    .filter((nome): nome is string => Boolean(nome));

  const recursoNome =
    recursoTextoDireto ||
    (recursosNomes.length > 0 ? recursosNomes.join(", ") : "");

  const tipoSolicitante = getFirstMatch(item, ["tipoSolicitante"], toOptionalNumber) ?? 1;

  const nomeTerceiro =
    getFirstMatch(
      item,
      ["nomeSolicitanteTerceiro"],
      (value) => {
        const parsedValue = toStringSafe(value);
        return parsedValue || undefined;
      },
    );

  const nomeUsuario =
    getFirstMatch(
      item,
      ["nomePessoaSolicitante"],
      (value) => {
        const parsedValue = toStringSafe(value);
        return parsedValue || undefined;
      },
    );

  const solicitanteNome =
    (tipoSolicitante === 2 ? (nomeTerceiro ?? nomeUsuario) : nomeUsuario) ??
    getFirstMatch(
      item,
      ["solicitante", "nomeSolicitante", "pessoaSolicitante"],
      (value) => {
        const parsedValue = toStringSafe(value);
        return parsedValue || undefined;
      },
    ) ??
    "";

  const dataReserva =
    getFirstMatch(item, ["dataReserva", "data", "dataInicio"], (value) => {
      const parsedValue = toStringSafe(value);
      return parsedValue || undefined;
    }) ?? (dataHoraInicio ? dataHoraInicio.slice(0, 10) : "");

  const horaInicio =
    getFirstMatch(item, ["horaInicio", "horarioInicio"], (value) => {
      const parsedValue = toStringSafe(value);
      return parsedValue || undefined;
    }) ?? (dataHoraInicio ? dataHoraInicio.slice(11, 16) : "");

  const horaFim =
    getFirstMatch(item, ["horaFim", "horarioFim"], (value) => {
      const parsedValue = toStringSafe(value);
      return parsedValue || undefined;
    }) ?? (dataHoraFim ? dataHoraFim.slice(11, 16) : "");

  const arquivos = extrairArray(item.arquivos ?? item.Arquivos)
    .map(normalizarArquivoReserva)
    .filter((arquivo) => arquivo.idArquivo || arquivo.nomeArquivo);

  const podeAlterarUtilizada =
    getFirstMatch(
      item,
      ["podeAlterarUtilizada", "permiteAlterarUtilizada"],
      toOptionalBoolean,
    ) ?? !cancelada;

  const podeCancelar =
    getFirstMatch(
      item,
      ["podeCancelar", "permiteCancelar"],
      toOptionalBoolean,
    ) ?? !cancelada;

  const podeEditarSituacao =
    getFirstMatch(
      item,
      [
        "podeEditarSituacao",
        "podeEditar",
        "permiteEditar",
        "podeGerenciar",
        "permiteGerenciar",
      ],
      toOptionalBoolean,
    ) ??
    (podeAlterarUtilizada || podeCancelar);

  const podeVisualizar =
    getFirstMatch(
      item,
      ["podeVisualizar", "permiteVisualizar"],
      toOptionalBoolean,
    ) ?? true;

  return {
    id,
    numeroReserva,
    local: localNome,
    recurso: recursoNome,
    arquivos,
    dataReserva,
    horaInicio,
    horaFim,
    observacao:
      getFirstMatch(item, ["observacao", "descricao"], (value) => {
        const parsedValue = toStringSafe(value);
        return parsedValue || undefined;
      }) ?? "",
    solicitante: solicitanteNome,
    utilizada,
    cancelada,
    status,
    statusDescricao: statusDescricao || (cancelada ? "Cancelada" : "Ativa"),
    motivoCancelamento:
      getFirstMatch(item, ["motivoCancelamento", "motivo"], (value) => {
        const parsedValue = toStringSafe(value);
        return parsedValue || undefined;
      }) ?? "",
    dataCancelamento:
      getFirstMatch(
        item,
        ["dataCancelamento", "dataHoraCancelamento"],
        (value) => {
          const parsedValue = toStringSafe(value);
          return parsedValue || undefined;
        },
      ) ?? "",
    usuarioResponsavelCancelamento:
      getFirstMatch(
        item,
        [
          "nomePessoaCancelamento",
          "usuarioResponsavelCancelamento",
          "responsavelCancelamento",
          "canceladoPor",
          "idPessoaCancelamento",
        ],
        (value) => {
          const parsedValue = toStringSafe(value);
          return parsedValue || undefined;
        },
      ) ?? "",
    podeVisualizar,
    podeEditarSituacao,
    podeAlterarUtilizada,
    podeCancelar,
  };
};

const extrairListaMinhasReservas = (
  data: unknown,
): Record<string, unknown>[] => {
  if (Array.isArray(data)) {
    return data as Record<string, unknown>[];
  }

  if (data && typeof data === "object") {
    const registro = data as Record<string, unknown>;

    if (Array.isArray(registro.items)) {
      return registro.items as Record<string, unknown>[];
    }

    if (Array.isArray(registro.data)) {
      return registro.data as Record<string, unknown>[];
    }

    if (Array.isArray(registro.registros)) {
      return registro.registros as Record<string, unknown>[];
    }

    if (Array.isArray(registro.resultados)) {
      return registro.resultados as Record<string, unknown>[];
    }
  }

  return [];
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  const errorRecord = toRecord(error);
  const responseRecord = toRecord(errorRecord?.response);
  const responseDataRecord = toRecord(responseRecord?.data);

  const mensagemApi =
    getFirstMatch(
      responseDataRecord || {},
      ["message", "mensagem", "title", "erro"],
      (value) => {
        const parsedValue = toStringSafe(value);
        return parsedValue || undefined;
      },
    ) ??
    getFirstMatch(errorRecord || {}, ["message"], (value) => {
      const parsedValue = toStringSafe(value);
      return parsedValue || undefined;
    });

  return mensagemApi || fallback;
};

const getNomeArquivoContentDisposition = (
  header: unknown,
): string | undefined => {
  if (typeof header !== "string" || !header) {
    return undefined;
  }

  const matchUtf = header.match(/filename\*=UTF-8''([^;]+)/i);

  if (matchUtf?.[1]) {
    return decodeURIComponent(matchUtf[1].replace(/"/g, "")).trim();
  }

  const match = header.match(/filename="?([^";]+)"?/i);

  if (match?.[1]) {
    return match[1].trim();
  }

  return undefined;
};

const getDisponibilidade = async (
  endpoint: string,
  filtros: IFiltrosDisponibilidadeReserva,
): Promise<IListagemDisponibilidadeReserva | Error> => {
  try {
    const pagina = filtros.pagina ?? 1;
    const tamanhoPagina = filtros.tamanhoPagina ?? filtros.limite ?? 10;
    const idEstabelecimento =
      filtros.idEstabelecimento || filtros.estabelecimentoId;
    const idCategoria = filtros.idCategoria || filtros.categoriaId;
    const nome = filtros.nome || filtros.busca;

    const { data, headers } = await Api.get(endpoint, {
      params: {
        estabelecimentoId: idEstabelecimento || undefined,
        idEstabelecimento: idEstabelecimento || undefined,
        dataReserva: filtros.dataReserva,
        horaInicio: filtros.horaInicio,
        horaFim: filtros.horaFim,
        pagina,
        tamanhoPagina,
        idCategoria: idCategoria || undefined,
        nome: nome || undefined,
      },
    });

    const itemsBrutos = extrairArray(data);
    const items = itemsBrutos
      .map(normalizarItemDisponibilidade)
      .filter((item) => item.id !== "" && item.nomeExibicao !== "");

    const totalRegistros = extrairTotal(
      data,
      items.length,
      (headers as Record<string, unknown>) || {},
    );

    const paginaRetorno =
      (toRecord(data) &&
        getFirstMatch(
          toRecord(data) || {},
          ["pagina", "Pagina"],
          toOptionalNumber,
        )) ||
      pagina;

    const tamanhoPaginaRetorno =
      (toRecord(data) &&
        getFirstMatch(
          toRecord(data) || {},
          ["tamanhoPagina", "TamanhoPagina", "limite", "Limite"],
          toOptionalNumber,
        )) ||
      tamanhoPagina;

    return {
      items,
      totalRegistros,
      pagina: paginaRetorno,
      limite: tamanhoPaginaRetorno,
    };
  } catch (error: unknown) {
    console.error(error);
    return new Error(
      getErrorMessage(error, "Erro ao consultar disponibilidade."),
    );
  }
};

const getCategoriasDisponiveis = async (
  filtros: IFiltrosDisponibilidadeReserva,
  tipo: TTipoDisponibilidade,
): Promise<ICategoriaDisponivelReserva[] | Error> => {
  try {
    const { data } = await Api.get("/Categoria", {
      params: {
        tipo: tipoParaCodigo(tipo),
      },
    });

    const categorias = extrairArray(data)
      .map((item) => ({
        id:
          (item.id as string | number | undefined) ??
          (item.categoriaId as string | number | undefined) ??
          "",
        nomeExibicao:
          (item.nomeExibicao as string | undefined) ??
          (item.nome as string | undefined) ??
          (item.descricao as string | undefined) ??
          "",
      }))
      .filter((categoria) => categoria.id !== "" && categoria.nomeExibicao);

    return categorias;
  } catch (e: any) {
    try {
      const endpointFallback =
        tipo === "local"
          ? "/Reserva/CategoriasLocaisDisponiveis"
          : "/Reserva/CategoriasRecursosDisponiveis";
      const { data } = await Api.get(endpointFallback);

      const categorias = extrairArray(data)
        .map((item) => ({
          id:
            (item.id as string | number | undefined) ??
            (item.categoriaId as string | number | undefined) ??
            "",
          nomeExibicao:
            (item.nomeExibicao as string | undefined) ??
            (item.nome as string | undefined) ??
            (item.descricao as string | undefined) ??
            "",
        }))
        .filter((categoria) => categoria.id !== "" && categoria.nomeExibicao);

      return categorias;
    } catch {
      console.error(e);
      return new Error(e.message || "Erro ao consultar categorias.");
    }
  }
};

const getRecursosDisponiveis = async (
  filtros: IFiltrosDisponibilidadeReserva,
): Promise<IListagemDisponibilidadeReserva | Error> =>
  getDisponibilidade("/Reserva/RecursosDisponiveis", filtros);

const getLocaisDisponiveis = async (
  filtros: IFiltrosDisponibilidadeReserva,
): Promise<IListagemDisponibilidadeReserva | Error> =>
  getDisponibilidade("/Reserva/LocaisDisponiveis", filtros);

const getPessoas = async (
  nomePessoa: string,
): Promise<IPessoaSolicitanteReserva[] | Error> => {
  try {
    const { data } = await Api.get("/Reserva/Pessoas", {
      params: { nomePessoa },
    });

    return extrairArray(data)
      .map(normalizarPessoaSolicitante)
      .filter((pessoa) => pessoa.idPessoa > 0 && Boolean(pessoa.nome));
  } catch (e: any) {
    console.error(e);
    return new Error(e.message || "Erro ao consultar solicitantes.");
  }
};

const getPessoaUsuarioLogado = async (): Promise<IPessoaSolicitanteReserva[] | Error> => {
  try {
    const { data } = await Api.get("/Reserva/Pessoa/UsuarioLogado");

    return extrairArray(data)
      .map(normalizarPessoaSolicitante)
      .filter((pessoa) => pessoa.idPessoa > 0 && Boolean(pessoa.nome));
  } catch (e: any) {
    console.error(e);
    return new Error(e.message || "Erro ao consultar pessoa do usuário logado.");
  }
};

const getUsuarioLogadoInfo = async (
  auth?: { token?: string; tenant?: string },
): Promise<IUsuarioLogadoInfoReserva | Error> => {
  try {
    const { data } = await Api.get("/Reserva/UsuarioLogadoInfo", {
      // sobrescreve a sessão via cookie quando token/tenant vierem da URL
      ...((auth?.token || auth?.tenant) && {
        headers: {
          ...(auth?.token && { Authorization: `Bearer ${auth.token}` }),
          ...(auth?.tenant && { Idtenant: auth.tenant }),
        },
      }),
    });

    const idUsuario = Number(data?.idUsuario ?? 0);
    const nomeUsuario = String(data?.nomeUsuario ?? "").trim();
    const idsSetores = normalizarIdsSetores([
      ...extrairIdsSetores(data?.idsSetores),
      ...extrairIdsSetores(data?.idsSetoresUsuario),
    ]);

    const idTenant = String(data?.idTenant ?? "").trim();

    if (!idUsuario || !nomeUsuario) {
      return new Error("Dados do usuário logado inválidos.");
    }

    return {
      idUsuario,
      nomeUsuario,
      idsSetores,
      idTenant
    };
  } catch (e: any) {
    console.error(e);
    return new Error(e.message || "Erro ao consultar usuário logado.");
  }
};

const create = async (
  dados: ICriarReservaPayload,
): Promise<unknown | Error> => {
  try {
    const payload = await montarPayloadCriacaoReserva(dados);
    const { data } = await Api.post("/Reserva", payload);

    return data;
  } catch (e: any) {
    console.error(e);
    const detalhesErro =
      e?.response?.data ??
      e?.data ??
      e?.details ??
      e?.detalhes ??
      (e && typeof e === "object" ? e : undefined);

    const mensagemApi =
      detalhesErro?.message ||
      detalhesErro?.mensagem ||
      detalhesErro?.title ||
      detalhesErro?.erro ||
      e?.message ||
      "Erro ao criar reserva.";

    const erro = new Error(mensagemApi) as IErroCriarReserva;
    erro.status = e?.response?.status;
    erro.detalhes = detalhesErro;

    return erro;
  }
};

const getMinhasReservas = async (
  filtros: IFiltrosMinhasReservas,
): Promise<IListagemMinhasReservas | Error> => {
  try {
    const { data, headers } = await Api.get("/Reserva", {
      params: {
        data: filtros.data,
        numeroReserva: filtros.numeroReserva,
      },
    });

    const items = extrairListaMinhasReservas(data).map((item) =>
      normalizarReservaMinha(item),
    );
    const total = extrairTotal(
      data,
      items.length,
      (headers as Record<string, unknown>) || {},
    );

    return {
      items,
      total,
    };
  } catch (e: any) {
    console.error(e);
    return new Error(e.message || "Erro ao consultar reservas.");
  }
};

const getReservaDetalhe = async (
  idReserva: string,
): Promise<IReservaMinhaReserva | Error> => {
  try {
    const { data } = await Api.get(`/Reserva/${idReserva}`);

    if (data && typeof data === "object") {
      return normalizarReservaMinha(data as Record<string, unknown>);
    }

    return new Error("Detalhes da reserva não encontrados.");
  } catch (e: any) {
    console.error(e);
    return new Error(e.message || "Erro ao carregar detalhes da reserva.");
  }
};

const getIndicadoresMinhasReservas =
  async (): Promise<IIndicadoresMinhasReservas | Error> => {
  try {
    const { data } = await Api.get("/Reserva/Indicadores/MinhasReservas");

    const reservasParaHoje =
      getFirstMatch(
        (data || {}) as Record<string, unknown>,
        ["reservasParaHoje", "reservasHoje", "hoje", "ativosHoje"],
        toOptionalNumber,
      ) ?? 0;
    const reservasFuturas =
      getFirstMatch(
        (data || {}) as Record<string, unknown>,
        ["reservasFuturas", "futuras", "ativasFuturas"],
        toOptionalNumber,
      ) ?? 0;

    return {
      reservasParaHoje,
      reservasFuturas,
    };
  } catch (e: any) {
    console.error(e);
    return new Error(e.message || "Erro ao consultar indicadores.");
  }
};

const atualizarSituacaoReserva = async (
  idReserva: string,
  payload: IAtualizarSituacaoReservaPayload,
): Promise<IReservaMinhaReserva | Error> => {
  try {
    const { data } = await Api.patch(`/Reserva/${idReserva}`, payload);

    if (data && typeof data === "object") {
      return normalizarReservaMinha(data as Record<string, unknown>);
    }

    const detalhe = await getReservaDetalhe(idReserva);

    if (detalhe instanceof Error) {
      return {
        id: idReserva,
        numeroReserva: "",
        local: "",
        recurso: "",
        arquivos: [],
        dataReserva: "",
        horaInicio: "",
        horaFim: "",
        observacao: "",
        solicitante: "",
        utilizada: payload.utilizada,
        cancelada: payload.status === STATUS_CANCELADA,
        status: payload.status,
        statusDescricao:
          payload.status === STATUS_CANCELADA ? "Cancelada" : "Ativa",
        motivoCancelamento: payload.motivoCancelamento,
        dataCancelamento: "",
        usuarioResponsavelCancelamento: "",
        podeVisualizar: true,
        podeEditarSituacao: payload.status === STATUS_ATIVA,
        podeAlterarUtilizada: payload.status === STATUS_ATIVA,
        podeCancelar: payload.status === STATUS_ATIVA,
      };
    }

    return detalhe;
  } catch (e: any) {
    console.error(e);
    return new Error(e.message || "Erro ao atualizar reserva.");
  }
};

const downloadArquivoReserva = async (
  idArquivo: string,
  nomeArquivoFallback: string,
): Promise<IDownloadArquivoReservaResponse | Error> => {
  try {
    const { data, headers } = await Api.get(`/Reserva/Arquivo/${idArquivo}`, {
      responseType: "blob",
    });

    const nomeArquivo =
      getNomeArquivoContentDisposition(headers?.["content-disposition"]) ||
      nomeArquivoFallback ||
      "arquivo";

    return {
      arquivo: data as Blob,
      nomeArquivo,
    };
  } catch (error: unknown) {
    console.error(error);
    return new Error(
      getErrorMessage(error, "Erro ao baixar arquivo da reserva."),
    );
  }
};

export const ReservaService = {
  create,
  getCategoriasDisponiveis,
  getRecursosDisponiveis,
  getLocaisDisponiveis,
  getPessoas,
  getPessoaUsuarioLogado,
  getUsuarioLogadoInfo,
  getMinhasReservas,
  getReservaDetalhe,
  getIndicadoresMinhasReservas,
  atualizarSituacaoReserva,
  downloadArquivoReserva,
};
